import { useState, SetStateAction, Dispatch } from 'react'
import { useQuery, ApolloError } from '@apollo/client'
import { PrivacyNotice, SignUpNotices } from '@babylon/consumer-api/types'

import { ParsedGraphQLError } from '@/types/graphql'
import ConsumerNetworkNotices from '@/queries/ConsumerNetworkNotices'
import SignupNotices from '@/queries/SignupNotices'
import { blacklistedNoticeTypes, linksNoticeTypes } from '@/constants'

type ConsumerNetworkNoticeResponse = {
  consumerNetworkNotices: PrivacyNotice[]
}

type SignupNoticesResponse = {
  signupNotices: SignUpNotices
}

interface Notices {
  checkboxes?: PrivacyNotice[]
  links?: PrivacyNotice[]
}

type UserPrivacyNoticesResponse = {
  notices: Notices | undefined
  loading: boolean
  error?: ParsedGraphQLError
}

type UserPrivacyNoticesResponseWithRefetch = UserPrivacyNoticesResponse & {
  refetch: Dispatch<SetStateAction<string>>
}

const getError = (error?: ApolloError): ParsedGraphQLError['parsedError'] => {
  const graphQlError = error?.graphQLErrors as ParsedGraphQLError[]

  return graphQlError?.[1]?.parsedError
}

const splitNoticesByVisualType = (notices: PrivacyNotice[]) => {
  const checkboxes = notices.filter(
    (notice) => !linksNoticeTypes.includes(notice.noticeType!)
  ) as PrivacyNotice[]

  const links = notices.filter((notice) =>
    linksNoticeTypes.includes(notice.noticeType!)
  ) as PrivacyNotice[]

  return {
    checkboxes,
    links,
  }
}

const getFilteredNotices = (notices?: PrivacyNotice[]) => {
  if (!notices) {
    return undefined
  }

  const filteredNotices = notices
    .filter((item) => item)
    .filter(
      (notice) => !blacklistedNoticeTypes.includes(notice.noticeType!)
    ) as PrivacyNotice[]

  return splitNoticesByVisualType(filteredNotices)
}

const usePrivacyNotices = (
  membershipCode: string
): UserPrivacyNoticesResponseWithRefetch => {
  const [code, setCode] = useState<string>(membershipCode)

  const {
    data: { consumerNetworkNotices } = {},
    loading: consumerNetworkNoticesLoading,
    error: consumerNetworkNoticesError,
  } = useQuery<ConsumerNetworkNoticeResponse>(ConsumerNetworkNotices, {
    skip: !code,
    fetchPolicy: 'network-only',
    context: {
      credentials: 'omit',
    },
    variables: {
      promocode: code,
    },
  })

  const {
    data: { signupNotices } = {},
    loading: signupNoticesLoading,
    error: signupNoticesError,
  } = useQuery<SignupNoticesResponse>(SignupNotices, {
    skip: Boolean(code),
    fetchPolicy: 'network-only',
    context: {
      credentials: 'omit',
    },
  })

  let returnValue: UserPrivacyNoticesResponse

  if (code) {
    returnValue = {
      notices: getFilteredNotices(consumerNetworkNotices),
      loading: consumerNetworkNoticesLoading,
      error: getError(consumerNetworkNoticesError),
    }
  } else {
    let checkboxes: PrivacyNotice[] | undefined
    let links: PrivacyNotice[] | undefined

    if (signupNotices) {
      const { terms, privacyPolicy, marketing } = signupNotices
      checkboxes = [terms, marketing].filter((item) => item) as PrivacyNotice[]
      links = privacyPolicy as PrivacyNotice[] | undefined
    }

    returnValue = {
      notices: {
        checkboxes,
        links,
      },
      loading: signupNoticesLoading,
      error: getError(signupNoticesError),
    }
  }

  return {
    ...returnValue,
    refetch: setCode,
  }
}

export default usePrivacyNotices
